import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { useCachedSelectedProject, useTranslation } from 'hooks';
import { useLocation } from 'react-router-dom';
import Drawer from 'shared/ui/drawer/drawer';
import { Card } from 'shared/ui/card/card';
import { Chip } from 'shared/ui/chip/chip';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { TechRulesAssesment } from 'components/tech-rules-assessment/tech-rules-assessment';
import { RuleDescription } from 'widgets/score-disclosure/components/rules/rule-description';
import { AnalysisItem } from 'shared/ui/suggestion/analysisItem';
import { ScoreSquare } from 'shared/ui/score-square/score-square';
import { WrappedIcon } from 'shared/ui/wrapped-icon/wrapped-icon';
import { getPagePrefix, track } from 'utils/mixpanel';
import { getRuleTrackName } from 'widgets/score-disclosure/helpers';
import {
  RuleExample,
  ScoreEnum,
  InfoCompletenessScoring,
} from 'store/api/apiTypes';
import { renderFindingsRoot } from './renderFindings';
import { linkifyText } from 'shared/helpers/linkifyText';
import {
  getScoreDisplayConfig,
  isMaxScoreReached,
} from 'shared/helpers/ruleScoring';
import { getTechnicalFindingsViewMode } from 'shared/helpers/technicalFindingsViewMode';
import { ExternalLink } from 'shared/ui/external-link/external-link';
import { TrialBanner } from 'shared/ui/trial-banner/trial-banner';
import cls from './rule-drawer.module.scss';

export type RuleDrawerVariant = 'technical' | 'product' | 'analysis';

interface RuleDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  score?: ScoreEnum;
  analysisScore?: number;
  showScore?: boolean;
  showChip?: boolean;
  analysis?: RuleExample[];
  showAnalysis?: boolean;
  reverse?: boolean;
  variant?: RuleDrawerVariant;
  typeName?: string;
  productNumber?: number;
  reference?: string;
  analysisRule?: InfoCompletenessScoring;
  ruleDescriptionContent?: React.ReactNode;
  isTechnicalAccessibility?: boolean;
  maxScore?: number;
}

const renderStatusText = (text: string): React.ReactNode => {
  const lines = text
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  if (lines.length <= 1) {
    return <p>{linkifyText(text)}</p>;
  }

  return (
    <ul className={cls.statusList}>
      {lines.map((line, index) => (
        <li key={`${line}-${index}`}>{linkifyText(line)}</li>
      ))}
    </ul>
  );
};

const isBooleanFindingsMap = (
  findings: unknown
): findings is Record<string, boolean> => {
  if (!findings || typeof findings !== 'object' || Array.isArray(findings)) {
    return false;
  }

  const values = Object.values(findings as Record<string, unknown>);
  return values.length > 0 && values.every(value => typeof value === 'boolean');
};

const shouldRenderFindingsDetails = (
  mode: 'text' | 'matrix',
  analysisRule?: InfoCompletenessScoring
): boolean => mode === 'matrix' && isBooleanFindingsMap(analysisRule?.findings);

export const RuleDrawer: React.FC<RuleDrawerProps> = ({
  isOpen,
  onClose,
  title,
  description,
  score = 1,
  analysisScore = 0,
  showChip = true,
  analysis = [],
  showAnalysis = true,
  reverse = false,
  variant = 'product',
  typeName,
  productNumber,
  reference = 'academicReference',
  analysisRule,
  ruleDescriptionContent,
  isTechnicalAccessibility = false,
  maxScore,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isTrial, testingB, selectedProject } = useCachedSelectedProject();

  const ruleName = getRuleTrackName(title);
  const pagePrefix = getPagePrefix(location.pathname);

  const scoreTitle = (
    {
      1: t('bad'),
      2: t('average'),
      3: t('good'),
    } as const
  )[score];

  const scoreColor =
    (
      {
        1: 'red',
        2: 'orange',
        3: 'green',
      } as const
    )[score] ?? 'gray';

  const trackDisclosureClick = (cardType: string, open: boolean) => {
    track(
      !open
        ? `AIS:${pagePrefix}_CRD_ANALYSIS_SUBSECTION-EXPANDED`
        : `AIS:${pagePrefix}_CRD_ANALYSIS_SUBSECTION-COLAPSED`,
      {
        is_trial: isTrial,
        type: typeName ?? ruleName,
        card_type: cardType,
        ...(productNumber && { product: productNumber }),
        ...(typeName && { sub_type: ruleName }),
      }
    );
  };

  const handleClose = () => {
    track(`AIS:${pagePrefix}_CRD_ANALYSIS-CLOSED`, {
      type: typeName ?? ruleName,
      is_trial: isTrial,
      ...(productNumber && { product: productNumber }),
      ...(typeName && { sub_type: ruleName }),
    });
    onClose();
  };

  const isTechnical = variant === 'technical';
  const isAnalysis = variant === 'analysis';
  const isMaxScore = isMaxScoreReached(analysisScore, maxScore);
  const findingsViewMode = isTechnicalAccessibility
    ? getTechnicalFindingsViewMode({
        ruleKey: title,
        level: analysisRule?.level,
      })
    : isMaxScore
      ? 'text'
      : 'matrix';
  const shouldShowFindingsDetails = shouldRenderFindingsDetails(
    findingsViewMode,
    analysisRule
  );
  const shouldShowImprovementBlock = analysisRule?.level !== 'high';
  const domainUrl = selectedProject?.domain
    ? `https://${selectedProject.domain}`
    : '';
  const technicalAccessibilityScoreConfig =
    isTechnicalAccessibility && analysisRule
      ? getScoreDisplayConfig(analysisScore, maxScore)
      : undefined;

  if (isAnalysis && isTechnicalAccessibility && analysisRule) {
    return (
      <Drawer title={t(title)} isOpen={isOpen} onClose={handleClose}>
        <div className={cls.drawerContent}>
          {ruleDescriptionContent && (
            <Disclosure>
              {({ open }) => (
                <Card className={cls.discCard}>
                  <DisclosureButton
                    className={cls.disclosureButton}
                    onClick={() =>
                      trackDisclosureClick('Rule Description', open)
                    }
                  >
                    <h4>{t('ruleDescription')}</h4>
                    <div className={cls.right}>
                      <Chip color='blue' iconPath={ICONS.book}>
                        <span>{t('officialReference')}</span>
                      </Chip>
                      <Icon
                        pathToIcon={ICONS.arrowUp}
                        size={20}
                        className={`${cls.arrow} ${open ? cls.open : ''}`}
                      />
                    </div>
                  </DisclosureButton>
                  <DisclosurePanel className={cls.panel}>
                    {ruleDescriptionContent}
                  </DisclosurePanel>
                </Card>
              )}
            </Disclosure>
          )}

          <Disclosure defaultOpen>
            {({ open }) => (
              <Card className={cls.discCard}>
                <DisclosureButton
                  className={cls.disclosureButton}
                  onClick={() =>
                    trackDisclosureClick('Status Description', open)
                  }
                >
                  <h4>{t(analysisRule.status_title || title)}</h4>
                  <div className={cls.right}>
                    <Icon
                      pathToIcon={ICONS.arrowUp}
                      size={20}
                      className={`${cls.arrow} ${open ? cls.open : ''}`}
                    />
                  </div>
                </DisclosureButton>
                <DisclosurePanel className={cls.panel}>
                  <p>{t(analysisRule.status_description || '')}</p>
                </DisclosurePanel>
              </Card>
            )}
          </Disclosure>

          <Disclosure defaultOpen>
            {({ open }) => (
              <Card className={cls.discCard}>
                <DisclosureButton
                  className={cls.disclosureButton}
                  onClick={() => trackDisclosureClick('Rule Assessment', open)}
                >
                  <h4>{t('ruleAssessment')}</h4>
                  <div className={cls.right}>
                    {technicalAccessibilityScoreConfig && (
                      <div className={cls.statusChip}>
                        <Chip color={technicalAccessibilityScoreConfig.color}>
                          <span className={cls.statusChipLabel}>
                            {t(technicalAccessibilityScoreConfig.labelKey)}
                          </span>
                        </Chip>
                      </div>
                    )}
                    <Icon
                      pathToIcon={ICONS.arrowUp}
                      size={20}
                      className={`${cls.arrow} ${open ? cls.open : ''}`}
                    />
                  </div>
                </DisclosureButton>
                <DisclosurePanel className={cls.panel}>
                  <p>{linkifyText(t(analysisRule.reasoning))}</p>
                  <div>
                    <Card className={cls.websiteCard}>
                      <div
                        className={`${cls.column} ${isMaxScore ? cls.fullWidth : ''}`}
                      >
                        <div>
                          <div className={cls.case}>
                            <h4>{t('findings')}</h4>
                          </div>
                          <div className={cls.findingsContainer}>
                            {shouldShowFindingsDetails ? (
                              renderFindingsRoot(analysisRule.findings)
                            ) : (
                              <p className={cls.findingsText}>
                                {t(analysisRule.findings_text || '')}
                              </p>
                            )}
                          </div>
                        </div>
                        {domainUrl && (
                          <ExternalLink href={domainUrl}>
                            <Icon pathToIcon={ICONS.link} size={16} />
                            {domainUrl}
                          </ExternalLink>
                        )}
                      </div>
                      {shouldShowImprovementBlock && (
                        <div className={cls.right}>
                          <div className={cls.status}>
                            <WrappedIcon
                              pathToIcon={ICONS.cross}
                              color={'red'}
                              size={24}
                            />
                            <div>
                              <h4>{t('whatIsMissing')}</h4>
                              <p>
                                {linkifyText(t(analysisRule.what_is_missing))}
                              </p>
                            </div>
                          </div>
                          <div className={cls.status}>
                            <WrappedIcon
                              pathToIcon={ICONS.checkMarkGreen}
                              color={'green'}
                              size={24}
                            />
                            <div>
                              <h4>{t('howToImprove')}</h4>
                              {renderStatusText(t(analysisRule.how_to_improve))}
                            </div>
                          </div>
                        </div>
                      )}
                      {isTrial && !testingB && (
                        <div className={cls.trialOverlay}></div>
                      )}
                    </Card>
                    {isTrial && !testingB && (
                      <TrialBanner
                        title={t('trialBanner')}
                        description={t('trialBannerDescription')}
                        onActionClick={() =>
                          track('AIS:DSH_CRD_ANALYSIS-UPGRADE_BTN-CLICKED', {
                            type: typeName,
                            sub_type: ruleName,
                          })
                        }
                      />
                    )}
                  </div>
                </DisclosurePanel>
              </Card>
            )}
          </Disclosure>
        </div>
      </Drawer>
    );
  }

  if (isAnalysis && analysisRule) {
    return (
      <Drawer title={t(title)} isOpen={isOpen} onClose={handleClose}>
        <div className={cls.drawerContent}>
          <Disclosure>
            {({ open }) => (
              <Card className={cls.discCard}>
                <DisclosureButton
                  className={cls.disclosureButton}
                  onClick={() => trackDisclosureClick('Rule Description', open)}
                >
                  <h4>{t('ruleDescription')}</h4>
                  <div className={cls.right}>
                    <Chip color='blue' iconPath={ICONS.book}>
                      <span>{t('officialReference')}</span>
                    </Chip>
                    <Icon
                      pathToIcon={ICONS.arrowUp}
                      size={20}
                      className={`${cls.arrow} ${open ? cls.open : ''}`}
                    />
                  </div>
                </DisclosureButton>
                <DisclosurePanel className={cls.panel}>
                  {ruleDescriptionContent}
                </DisclosurePanel>
              </Card>
            )}
          </Disclosure>

          <Disclosure defaultOpen>
            {({ open }) => (
              <Card className={cls.discCard}>
                <DisclosureButton
                  className={cls.disclosureButton}
                  onClick={() => trackDisclosureClick('Rule Assessment', open)}
                >
                  <h4>{t('ruleAssessment')}</h4>
                  <div className={cls.right}>
                    <ScoreSquare score={analysisScore} maxScore={maxScore} />
                    <Icon
                      pathToIcon={ICONS.arrowUp}
                      size={20}
                      className={`${cls.arrow} ${open ? cls.open : ''}`}
                    />
                  </div>
                </DisclosureButton>
                <DisclosurePanel className={cls.panel}>
                  <p>{linkifyText(analysisRule.reasoning)}</p>
                  <div>
                    <Card className={cls.websiteCard}>
                      <div className={cls.column}>
                        <div className={cls.case}>
                          <h4>{t('findings')}</h4>
                        </div>
                        <div className={cls.findingsContainer}>
                          {renderFindingsRoot(analysisRule.findings)}
                        </div>
                      </div>
                      <div className={cls.right}>
                        <div className={cls.status}>
                          <WrappedIcon
                            pathToIcon={ICONS.cross}
                            color={'red'}
                            size={24}
                          />
                          <div>
                            <h4>{t('whatIsMissing')}</h4>
                            <p>
                              {linkifyText(t(analysisRule.what_is_missing))}
                            </p>
                          </div>
                        </div>
                        <div className={cls.status}>
                          <WrappedIcon
                            pathToIcon={ICONS.checkMarkGreen}
                            color={'green'}
                            size={24}
                          />
                          <div>
                            <h4>{t('howToImprove')}</h4>
                            <p>{linkifyText(t(analysisRule.how_to_improve))}</p>
                          </div>
                        </div>
                      </div>
                      {isTrial && !testingB && (
                        <div className={cls.trialOverlay}></div>
                      )}
                    </Card>
                    {isTrial && !testingB && (
                      <TrialBanner
                        title={t('trialBanner')}
                        description={t('trialBannerDescription')}
                        onActionClick={() =>
                          track('AIS:DSH_CRD_ANALYSIS-UPGRADE_BTN-CLICKED', {
                            type: typeName,
                            sub_type: ruleName,
                          })
                        }
                      />
                    )}
                  </div>
                </DisclosurePanel>
              </Card>
            )}
          </Disclosure>
        </div>
      </Drawer>
    );
  }

  if (!isTechnical) {
    return (
      <Drawer title={t(title)} isOpen={isOpen} onClose={handleClose}>
        <div className={`${cls.drawerContent} ${reverse ? cls.reverse : ''}`}>
          <Disclosure defaultOpen>
            {({ open }) => (
              <Card className={cls.discCard}>
                <DisclosureButton
                  className={cls.disclosureButton}
                  onClick={() => trackDisclosureClick('Rule Assessment', open)}
                >
                  <h4>{t('ruleAssessment')}</h4>
                  <div className={cls.right}>
                    <Chip color={showChip ? scoreColor : 'gray'}>
                      {showChip ? (
                        <span>{scoreTitle}</span>
                      ) : (
                        <span>{t('analysisBeAvailableSoon')}</span>
                      )}
                    </Chip>
                    <Icon
                      pathToIcon={ICONS.arrowDown}
                      size={20}
                      className={open ? cls.open : ''}
                    />
                  </div>
                </DisclosureButton>
                <DisclosurePanel className={cls.panel}>
                  {linkifyText(description)}
                </DisclosurePanel>
              </Card>
            )}
          </Disclosure>

          <Disclosure>
            {({ open }) => (
              <Card className={cls.discCard}>
                <DisclosureButton
                  className={cls.disclosureButton}
                  onClick={() => trackDisclosureClick('Rule Description', open)}
                >
                  <h4>{t('ruleDescription')}</h4>
                  <div className={cls.right}>
                    <Chip color='blue' iconPath={ICONS.book}>
                      <span>{t(reference)}</span>
                    </Chip>
                    <Icon
                      pathToIcon={ICONS.arrowDown}
                      size={20}
                      className={open ? cls.open : ''}
                    />
                  </div>
                </DisclosureButton>
                <DisclosurePanel className={cls.panel}>
                  <RuleDescription
                    ruleTitle={title}
                    productNumber={productNumber}
                  />
                </DisclosurePanel>
              </Card>
            )}
          </Disclosure>

          {showAnalysis && (
            <Disclosure defaultOpen>
              {({ open }) => (
                <Card className={cls.discCard}>
                  <DisclosureButton
                    className={cls.disclosureButton}
                    onClick={() =>
                      trackDisclosureClick('Website Analysis', open)
                    }
                  >
                    <h4>{t('websiteAnalysis')}</h4>
                    <div className={cls.right}>
                      <Chip color='blue' iconPath={ICONS.assignment}>
                        <span>
                          {analysis.length} {t('casesFromWebsite')}
                        </span>
                      </Chip>
                      <Icon
                        pathToIcon={ICONS.arrowDown}
                        size={20}
                        className={open ? cls.open : ''}
                      />
                    </div>
                  </DisclosureButton>
                  <DisclosurePanel className={cls.panel}>
                    <p>
                      {t('websiteAnalysisDescription', undefined, undefined, {
                        count: analysis.length,
                      })}
                    </p>
                    {isTrial && !testingB ? (
                      <div className={cls.cardsContainer}>
                        {analysis[0] && (
                          <>
                            <AnalysisItem
                              item={analysis[0]}
                              index={0}
                              isTrial
                            />
                            <TrialBanner
                              title={t('trialBanner')}
                              description={t('trialBannerDescription')}
                            />
                          </>
                        )}
                      </div>
                    ) : (
                      <div className={cls.cardsContainer}>
                        {analysis.map((item, index) => (
                          <AnalysisItem
                            key={index}
                            item={item}
                            index={index}
                            productNumber={productNumber}
                            ruleName={ruleName}
                          />
                        ))}
                      </div>
                    )}
                  </DisclosurePanel>
                </Card>
              )}
            </Disclosure>
          )}
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer title={t(title)} isOpen={isOpen} onClose={handleClose}>
      <div className={`${cls.drawerContent} ${reverse ? cls.reverse : ''}`}>
        <Disclosure defaultOpen>
          {({ open }) => (
            <Card className={cls.discCard}>
              <DisclosureButton
                className={cls.disclosureButton}
                onClick={() => trackDisclosureClick('Rule Assessment', open)}
              >
                <h4>{t('ruleAssessment')}</h4>
                <div className={cls.right}>
                  <Chip color='gray'>
                    <span>{t('analysisBeAvailableSoon')}</span>
                  </Chip>
                  <Icon
                    pathToIcon={ICONS.arrowDown}
                    size={20}
                    className={open ? cls.open : ''}
                  />
                </div>
              </DisclosureButton>
              <DisclosurePanel className={cls.panel}>
                {linkifyText(description)}
                <Card className={cls.howToFix}>
                  <h4>{t('howToFix')}</h4>
                  <TechRulesAssesment
                    isTrial={isTrial && !testingB}
                    ruleKey={title}
                  />
                  {isTrial && !testingB && (
                    <TrialBanner
                      variant='subtleContrast'
                      title={t('trialBanner')}
                      description={t('trialBannerDescription')}
                      onActionClick={() =>
                        track(
                          `AIS:${pagePrefix}_CRD_DETAILS-UPGRADE_BTN-CLICKED`,
                          {
                            type: ruleName,
                            is_trial: isTrial,
                            ...(productNumber && {
                              product: productNumber,
                            }),
                          }
                        )
                      }
                    />
                  )}
                </Card>
              </DisclosurePanel>
            </Card>
          )}
        </Disclosure>

        <Disclosure>
          {({ open }) => (
            <Card className={cls.discCard}>
              <DisclosureButton
                className={cls.disclosureButton}
                onClick={() => trackDisclosureClick('Rule Description', open)}
              >
                <h4>{t('ruleDescription')}</h4>
                <div className={cls.right}>
                  <Chip color='blue' iconPath={ICONS.book}>
                    <span>{t('specificationReference')}</span>
                  </Chip>
                  <Icon
                    pathToIcon={ICONS.arrowDown}
                    size={20}
                    className={open ? cls.open : ''}
                  />
                </div>
              </DisclosureButton>
              <DisclosurePanel className={cls.panel}>
                <RuleDescription ruleTitle={title} />
              </DisclosurePanel>
            </Card>
          )}
        </Disclosure>
      </div>
    </Drawer>
  );
};
